import React, { useEffect, useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputBase,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { APIEndpoint } from 'common/enum';
import {
  universalGetRequest,
  universalPutRequest,
} from 'common/requestHandler';
import { FileData } from 'common/utils';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import colors from 'theme/colors';

import { popupNotification } from '../../notifications/ToastNotification';
import {
  copyFiles,
  moveFiles,
} from '../../screens/admin/adminDashboardService';
import { fileNameWithoutExtensionValidation } from '../../validation/validationScheme';
import CustomModal from '../CustomModal';
import CustomIconButton from '../elements/IconButton';
import InputElement from '../elements/InputElement';
import Loader from '../elements/Loader';
import OverlayLoader from '../elements/OverlayLoader';
import FileItem from './FileItem';
import { renameFile, searchFilesAndFoldersRecursive } from './utils';

// Define the Tag interface
interface Tag {
  name: string;
  value: string;
}

// Define the form data interface
interface TagFormData {
  tags: Tag[];
}

// Define the rename form data interface
interface RenameFormData {
  newName: string;
  deleteOldVersions: boolean;
}
interface Props {
  // path: string[];
  // setPath: React.Dispatch<React.SetStateAction<string[]>>;
  userFiles: FileData[];
  path: string[]; // Accept path as a prop
  setPath: React.Dispatch<React.SetStateAction<string[]>>;
  loading?: boolean;
  getFileVersions: (file: FileData) => void;
  isPrivate?: boolean;
  onRefreshFiles?: () => void; // Callback to refresh files after copy
  userRole?: string; // User role for copy API payload
  currentUserId?: string; // Current user/corporate ID for destination path
}

//test data

const FileTable: React.FC<Props> = ({
  userFiles,
  path,
  setPath,
  loading = false,
  getFileVersions,
  isPrivate = false,
  onRefreshFiles,
  userRole,
  currentUserId,
}) => {
  const { uuid, '*': dynamicPath } = useParams();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // const [path, setPath] = useState<string[]>(
  //   dynamicPath ? dynamicPath.split('/').filter(Boolean) : []
  // );
  const location = useLocation();
  const rootPath = location.pathname.split('/')[2];
  const [currentFolder, setCurrentFolder] = useState<FileData | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set()); // To store selected items
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<FileData | null>(null);

  // State for tag modal
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [loadingExistingTags, setLoadingExistingTags] = useState(false);

  // State for rename modal
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renamingFile, setRenamingFile] = useState(false);
  const [fileToRename, setFileToRename] = useState<FileData | null>(null);

  // State for copy functionality
  const [copiedFiles, setCopiedFiles] = useState<FileData[]>([]);
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const [showPasteButton, setShowPasteButton] = useState(false);
  const [copyingFiles, setCopyingFiles] = useState(false);

  const [movedFiles, setMovedFiles] = useState<FileData[]>([]);
  const [showMoveMessage, setShowMoveMessage] = useState(false);
  const [showMoveButton, setShowMoveButton] = useState(false);
  const [movingFiles, setMovingFiles] = useState(false);

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TagFormData>({
    defaultValues: {
      tags: [{ name: '', value: '' }],
    },
  });

  // Rename form setup
  const {
    control: renameControl,
    handleSubmit: handleRenameSubmit,
    reset: resetRenameForm,
    setValue: setRenameValue,
    formState: { errors: renameErrors },
  } = useForm<RenameFormData>({
    defaultValues: {
      newName: '',
      deleteOldVersions: false,
    },
  });

  const watchedTags = watch('tags');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (dynamicPath) {
      const newPath = dynamicPath.split('/').filter(Boolean);
      setPath(newPath);
    } else {
      setPath([]);
    }
  }, [dynamicPath]);

  // Keyboard shortcut for search (Ctrl+F)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    const fetchFilesData = async () => {
      let folderData: FileData[] = userFiles;
      for (const folder of path) {
        const folderItem = folderData.find(
          (item) => item.type === 'folder' && item.name === folder
        );
        if (folderItem && folderItem.items) {
          folderData = folderItem.items;
        } else {
          folderData = [];
        }
      }

      const folder: FileData | null =
        folderData.length > 0
          ? {
              id: 'dummyId',
              type: 'folder',
              name: 'Current Folder',
              modified: 'N/A',
              items: folderData,
            }
          : null;
      setCurrentFolder(folder);
    };

    fetchFilesData();
  }, [path, userFiles]);

  // Search files and folders recursively when there's a search term
  const searchResults = debouncedSearchTerm.trim()
    ? searchFilesAndFoldersRecursive(userFiles, debouncedSearchTerm)
    : [];

  // Use search results if searching, otherwise use userFiles filtered by current path
  const filteredItems = debouncedSearchTerm.trim()
    ? searchResults.map((result) => result)
    : (() => {
        // Filter userFiles by current path to preserve tags
        let items = userFiles;
        for (const folder of path) {
          const folderItem = items.find(
            (item) => item.type === 'folder' && item.name === folder
          );
          if (folderItem && folderItem.items) {
            items = folderItem.items;
          } else {
            items = [];
            break;
          }
        }
        return items;
      })();

  const handleSort = () => {
    if (currentFolder?.items) {
      const sortedData = [...filteredItems].sort((a, b) => {
        if (a.name < b.name) return sortDirection === 'asc' ? -1 : 1;
        if (a.name > b.name) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      setCurrentFolder((prev) =>
        prev ? { ...prev, items: sortedData } : null
      );
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };

  const handleFolderClick = (folderName: string) => {
    const newPath = [...path, folderName];
    setPath(newPath);
    navigate(`/${uuid}/${rootPath}/${newPath.join('/')}`);
  };

  const downloadFile = async (file: FileData) => {
    const response: any = await universalGetRequest(APIEndpoint.downloadFile, {
      fileKey: file['file-path'],
    });
    const presignedUrl = response.data;
    const link = document.createElement('a');
    link.href = presignedUrl;

    link.setAttribute('download', 'file_name.ext');

    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  };

  const handlePathClick = (index: number) => {
    const newPath = path.slice(0, index + 1);
    setPath(newPath);
    navigate(`/${uuid}/${rootPath}/${newPath.join('/')}`);
  };

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  // Function to load existing tags for a file from the file object
  const loadExistingTagsForFile = (file: FileData) => {
    if (!file['file-path']) {
      console.error('File path is missing');
      return;
    }

    try {
      setLoadingExistingTags(true);

      // Use existing tags from the file object instead of making API call
      const existingTags = file.tags || [];

      // Convert tags from {Key, Value} format to {name, value} format for the form
      const formattedTags = existingTags
        .map((tag) => ({
          name: tag.Key || '',
          value: tag.Value || '',
        }))
        .filter((tag) => {
          // Filter out tags with invalid values like "None", null, undefined, or empty strings
          const isValidName =
            tag.name && tag.name !== 'None' && tag.name.trim() !== '';
          const isValidValue =
            tag.value && tag.value !== 'None' && tag.value.trim() !== '';
          return isValidName && isValidValue;
        });

      // If there are existing tags, use them; otherwise start with one empty row
      if (formattedTags.length > 0) {
        setValue('tags', formattedTags);
      } else {
        setValue('tags', [{ name: '', value: '' }]);
      }
    } catch (error) {
      console.error('Error loading existing tags:', error);
      // On error, start with one empty row
      setValue('tags', [{ name: '', value: '' }]);
    } finally {
      setLoadingExistingTags(false);
    }
  };

  // Handler for opening tag modal
  const handleAddTag = (file: FileData) => {
    setSelectedFile(file);
    setTagModalOpen(true);
    // Load existing tags from file object when modal opens
    loadExistingTagsForFile(file);
  };

  // Handler for copy action
  const handleCopy = (file: FileData) => {
    setCopiedFiles([file]);
    setShowCopyMessage(true);
    setShowPasteButton(true);

    // Hide the copy message after 3 seconds
    setTimeout(() => {
      setShowCopyMessage(false);
    }, 3000);
  };

  // Handler for move action
  const handleMove = (file: FileData) => {
    setMovedFiles([file]);
    setShowMoveMessage(true);
    setShowMoveButton(true);

    // Hide the move message after 3 seconds
    setTimeout(() => {
      setShowMoveMessage(false);
    }, 3000);
  };

  // Handler for paste action
  const handlePaste = async () => {
    if (copiedFiles.length === 0) return;

    try {
      setCopyingFiles(true);

      // Construct destination paths based on current context
      const currentPath = path.join('/');
      const filesToCopy = copiedFiles
        .filter((file) => file['file-path']) // Filter out files without file-path
        .map((file) => {
          const sourcePath = file['file-path']!;
          const fileName = file.name;

          // Extract the base path (everything before the filename)
          const sourceBasePath = sourcePath.replace(/\/[^\/]+$/, '');

          // Determine destination path based on context
          let destinationPath: string;

          if (isPrivate) {
            // Copying within work area (private-shared)
            if (currentPath) {
              destinationPath = `${sourceBasePath}/${currentPath}/${fileName}`;
            } else {
              destinationPath = `${sourceBasePath}/${fileName}`;
            }
          } else {
            // Copying to individual/corporate folder
            // The path structure should be: uuid/userId/Home/currentPath/fileName
            const pathSegments = sourceBasePath.split('/');
            const uuid = pathSegments[0]; // First segment is always UUID

            // Use currentUserId if available, otherwise extract from source path
            const userId = currentUserId || pathSegments[1];

            if (currentPath) {
              destinationPath = `${uuid}/${userId}/Home/${currentPath}/${fileName}`;
            } else {
              destinationPath = `${uuid}/${userId}/Home/${fileName}`;
            }
          }

          return {
            sourcePath,
            destinationPath,
          };
        });

      if (filesToCopy.length === 0) {
        popupNotification('No valid files to copy', false);
        return;
      }

      // Create a mock loading state setter since we don't have access to the main loading state
      const mockSetLoading = (updater: any) => {
        // This is a no-op since we're handling loading state locally
      };

      const result = await copyFiles(filesToCopy, mockSetLoading, userRole);

      if (result.success) {
        // Clear copied files and hide paste button
        setCopiedFiles([]);
        setShowPasteButton(false);
        setShowCopyMessage(false);

        popupNotification(result.message || 'Files copied successfully', true);

        // Refresh the file list
        if (onRefreshFiles) {
          onRefreshFiles();
        }
      } else {
        popupNotification(result.error || 'Failed to copy files', false);
      }
    } catch (error) {
      popupNotification('Failed to copy files', false);
    } finally {
      setCopyingFiles(false);
    }
  };

  // Handler for move action
  const handleMoveAction = async () => {
    if (movedFiles.length === 0) return;

    try {
      setMovingFiles(true);

      // Construct destination paths based on current context
      const currentPath = path.join('/');
      const filesToMove = movedFiles
        .filter((file) => file['file-path']) // Filter out files without file-path
        .map((file) => {
          const sourcePath = file['file-path']!;
          const fileName = file.name;

          // Extract the base path (everything before the filename)
          const sourceBasePath = sourcePath.replace(/\/[^\/]+$/, '');

          // Determine destination path based on context
          let destinationPath: string;

          if (isPrivate) {
            // Moving within work area (private-shared)
            if (currentPath) {
              destinationPath = `${sourceBasePath}/${currentPath}/${fileName}`;
            } else {
              destinationPath = `${sourceBasePath}/${fileName}`;
            }
          } else {
            // Moving to individual/corporate folder
            // The path structure should be: uuid/userId/Home/currentPath/fileName
            const pathSegments = sourceBasePath.split('/');
            const uuid = pathSegments[0]; // First segment is always UUID

            // Use currentUserId if available, otherwise extract from source path
            const userId = currentUserId || pathSegments[1];

            if (currentPath) {
              destinationPath = `${uuid}/${userId}/Home/${currentPath}/${fileName}`;
            } else {
              destinationPath = `${uuid}/${userId}/Home/${fileName}`;
            }
          }

          return {
            sourcePath,
            destinationPath,
          };
        });

      if (filesToMove.length === 0) {
        popupNotification('No valid files to move', false);
        return;
      }

      // Create a mock loading state setter since we don't have access to the main loading state
      const mockSetLoading = (updater: any) => {
        // This is a no-op since we're handling loading state locally
      };

      const result = await moveFiles(
        filesToMove,
        mockSetLoading,
        userRole,
        isPrivate,
        currentUserId
      );

      if (result.success) {
        // Clear moved files and hide move button
        setMovedFiles([]);
        setShowMoveButton(false);
        setShowMoveMessage(false);

        popupNotification(result.message || 'Files moved successfully', true);

        if (onRefreshFiles) {
          onRefreshFiles();
        }
      } else {
        popupNotification(result.error || 'Failed to move files', false);
      }
    } catch (error) {
      popupNotification('Failed to move files', false);
    } finally {
      setMovingFiles(false);
    }
  };

  // Handler for rename action
  const handleRename = (file: FileData) => {
    setFileToRename(file);
    // Extract filename without extension
    const fileNameWithoutExtension = file.name
      .split('.')
      .slice(0, -1)
      .join('.');
    setRenameValue('newName', fileNameWithoutExtension);
    setRenameValue('deleteOldVersions', false);
    setRenameModalOpen(true);
  };

  // Handler for rename submit
  const handleRenameSubmitAction = async (data: RenameFormData) => {
    if (!fileToRename || !fileToRename['file-path']) {
      popupNotification('No file selected for rename', false);
      return;
    }

    // Check if the new filename is different from the current filename
    const currentFileNameWithoutExtension = fileToRename.name
      .split('.')
      .slice(0, -1)
      .join('.');

    if (data.newName.trim() === currentFileNameWithoutExtension.trim()) {
      popupNotification(
        'New file name should be different from the current name',
        false
      );
      return;
    }

    try {
      setRenamingFile(true);

      // Construct the destination path by replacing the filename
      const sourcePath = fileToRename['file-path'];
      const pathParts = sourcePath.split('/');
      const fileName = pathParts.pop(); // Remove the filename
      const directory = pathParts.join('/');

      // Get the file extension from the original filename
      const fileExtension = fileName?.split('.').pop() || '';
      const newFullFileName = fileExtension
        ? `${data.newName}.${fileExtension}`
        : data.newName;
      const destinationPath = `${directory}/${newFullFileName}`;

      const result = await renameFile(
        sourcePath,
        destinationPath,
        data.deleteOldVersions
      );

      if (result.success) {
        popupNotification(result.message || 'File renamed successfully', true);
        setRenameModalOpen(false);
        resetRenameForm();
        // Refresh the file list
        // You might want to add a callback to refresh the files
      } else {
        popupNotification(result.error || 'Failed to rename file', false);
      }
    } catch (error) {
      popupNotification('Failed to rename file', false);
    } finally {
      setRenamingFile(false);
    }
  };

  // Handler for adding more tag rows
  const handleAddMoreTags = () => {
    if (watchedTags.length < 10) {
      setValue('tags', [...watchedTags, { name: '', value: '' }]);
    }
  };

  // Handler for removing tag rows
  const handleRemoveTag = (index: number) => {
    if (watchedTags.length > 1) {
      const newTags = watchedTags.filter((_, i) => i !== index);
      setValue('tags', newTags);
    }
  };

  // Handler for saving tags
  const handleSaveTags = async (data: TagFormData) => {
    try {
      setSavingTags(true);

      // Filter out empty tags
      const validTags = data.tags.filter(
        (tag: Tag) => tag.name.trim() && tag.value.trim()
      );
      const formattedTags = validTags
        .map((tag: Tag) => `${tag.name.trim()}=${tag.value.trim()}`)
        .join('&');

      if (!selectedFile || !selectedFile['file-path']) {
        return;
      }

      const payload = {
        httpMethod: 'PUT',
        actionName: 'KDW12D7WW',
        key: selectedFile['file-path'],
        tags: formattedTags,
      };

      const response = await universalPutRequest(
        APIEndpoint.secDocsMain,
        payload
      );

      if ('error' in response) {
        popupNotification('Failed to save tags', false, response.error);
      } else {
        popupNotification('Tags saved successfully', true, response.message);
      }

      setTagModalOpen(false);
      setLoadingExistingTags(false);
      reset();
    } catch (error) {
      setTagModalOpen(false);
      setLoadingExistingTags(false);
      reset();
    } finally {
      setSavingTags(false);
    }
  };

  return (
    <div>
      {/* Breadcrumbs: Display path, omit 'Root path' if at root level */}

      <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            setPath([]);
            navigate(`/${uuid}/${rootPath}/`);
          }}
          sx={{
            textDecoration: 'none',
            color: isPrivate ? colors.secondary : colors.primary,
          }}
        >
          {isPrivate ? 'private-shared' : 'Home'}
        </Link>
        {path.length > 0 && (
          <>
            <Typography variant="body2" component="span" color="gray">
              {' '}
              /{' '}
            </Typography>
            {path.map((segment, index) => (
              <React.Fragment key={segment}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => handlePathClick(index)}
                  sx={{
                    textDecoration: 'none',
                    color: isPrivate ? colors.secondary : colors.primary,
                  }}
                >
                  {segment}
                </Link>
                {index < path.length - 1 && (
                  <Typography variant="body2" component="span" color="gray">
                    {' '}
                    /{' '}
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </Typography>

      {/* Copy Message */}
      {showCopyMessage && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ fontSize: '0.875rem' }}
          >
            {copiedFiles.length} file{copiedFiles.length > 1 ? 's' : ''} copied.
            Navigate to destination and click Paste.
          </Typography>
        </Box>
      )}

      {/* Move Message */}
      {showMoveMessage && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ fontSize: '0.875rem' }}
          >
            {movedFiles.length} file{movedFiles.length > 1 ? 's' : ''} moved.
            Navigate to destination and click Move.
          </Typography>
        </Box>
      )}

      {/* Search Bar */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ position: 'relative', maxWidth: '400px', flex: 1 }}>
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'text.secondary',
              fontSize: 20,
            }}
          />
          <InputBase
            inputRef={searchInputRef}
            placeholder="Search files, folders, and tags... (Ctrl+F)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            endAdornment={
              searchTerm && (
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{
                    color: 'text.secondary',
                    p: 0.5,
                    mr: 1,
                    '&:hover': {
                      color: 'text.primary',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )
            }
            sx={{
              pl: 5,
              pr: searchTerm ? 6 : 2,
              py: 1,
              width: '100%',
              bgcolor: 'background.paper',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              fontSize: '0.875rem',
              '&:focus': {
                borderColor: '#3b82f6',
                boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
              },
            }}
          />
        </Box>

        {/* Paste Button */}
        {showPasteButton && (
          <CustomIconButton
            tooltipTitle="Paste copied files"
            buttonType="primary"
            size="medium"
            onClick={handlePaste}
            loading={copyingFiles}
            disabled={copyingFiles}
            sx={{ minWidth: 'auto' }}
          >
            <ContentPasteIcon />
          </CustomIconButton>
        )}

        {/* Move Button */}
        {showMoveButton && (
          <CustomIconButton
            tooltipTitle="Move files to current location"
            buttonType="primary"
            size="medium"
            onClick={handleMoveAction}
            loading={movingFiles}
            disabled={movingFiles}
            sx={{ minWidth: 'auto' }}
          >
            <DriveFileMoveIcon />
          </CustomIconButton>
        )}

        {debouncedSearchTerm.trim() && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontSize: '0.875rem' }}
          >
            Found {searchResults.length} item
            {searchResults.length !== 1 ? 's' : ''} in all folders
          </Typography>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: 8, boxShadow: '5px 10px 20px rgba(0, 0, 0, 0.1)' }}
      >
        <Table>
          <TableHead
            sx={{
              background: `linear-gradient(90deg, ${colors.gradientColor1} 0%, ${colors.gradientColor2} 100%);`,
            }}
          >
            <TableRow>
              {/* <TableCell>Select</TableCell> */}
              <TableCell sx={{ color: 'white' }}>Download</TableCell>
              <TableCell sx={{ color: 'white' }}>Type</TableCell>
              <TableCell sx={{ color: 'white' }}>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  Name
                  <IconButton onClick={handleSort} sx={{ ml: 1 }}>
                    {sortDirection === 'asc' ? (
                      <ArrowUpwardIcon sx={{ color: 'white' }} />
                    ) : (
                      <ArrowDownwardIcon sx={{ color: 'white' }} />
                    )}
                  </IconButton>
                </Typography>
              </TableCell>
              <TableCell sx={{ color: 'white' }}>Options</TableCell>
              <TableCell sx={{ color: 'white' }}>File scan status</TableCell>
              <TableCell sx={{ color: 'white' }}>Modified</TableCell>
              <TableCell sx={{ color: 'white' }}>Uploaded by</TableCell>
              <TableCell sx={{ color: 'white' }}>Size</TableCell>
              <TableCell sx={{ color: 'white' }}>Versions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : currentFolder &&
              Array.isArray(filteredItems) &&
              filteredItems.length > 0 ? (
              filteredItems?.map((file) => {
                // For search results, we need to handle navigation differently
                const handleItemClick = () => {
                  if (file.type === 'folder') {
                    if (debouncedSearchTerm.trim()) {
                      // If searching, navigate to the folder's full path and clear search
                      const fullPath = (file as any).fullPath || [file.name];
                      setPath(fullPath);
                      setSearchTerm(''); // Clear search when navigating
                      navigate(`/${uuid}/${rootPath}/${fullPath.join('/')}`);
                    } else {
                      // Normal folder navigation
                      handleFolderClick(file.name);
                    }
                  }
                };

                return (
                  <FileItem
                    key={file.id}
                    id={file.id}
                    type={file.type}
                    file={file}
                    name={
                      debouncedSearchTerm.trim() && (file as any).fullPath
                        ? `${(file as any).fullPath.join(' / ')}`
                        : file.name
                    }
                    modified={file.modified}
                    size={file.size}
                    uploadedBy={file['uploaded-by']}
                    onSelect={(checked) => handleSelect(file.id, checked)}
                    getFileVersions={() => getFileVersions(file)}
                    onClick={handleItemClick}
                    downloadFile={() => {
                      downloadFile(file);
                    }}
                    onOptionsClick={handleAddTag}
                    onCopyClick={handleCopy}
                    onMoveClick={handleMove}
                    onRenameClick={handleRename}
                  />
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {debouncedSearchTerm.trim() ? (
                    <Typography
                      variant="body2"
                      display="flex"
                      justifyContent="center"
                      alignContent="center"
                      gap={1}
                    >
                      No files or folders found matching "{debouncedSearchTerm}"
                      in any folder <FolderIcon sx={{ color: '#ffcc00' }} />
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      display="flex"
                      justifyContent="center"
                      alignContent="center"
                      gap={1}
                    >
                      No files or folders here{' '}
                      <FolderIcon sx={{ color: '#ffcc00' }} />
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Tag Modal */}
      <CustomModal
        open={tagModalOpen}
        onClose={() => {
          setTagModalOpen(false);
          setLoadingExistingTags(false);
          reset();
        }}
        type="info"
        title="Tags"
        autoClose={false}
        primaryButtonLabel={savingTags ? 'Saving...' : 'Save Tags'}
        primaryButtonAction={handleSubmit(handleSaveTags)}
        secondaryButtonLabel="Cancel"
        primaryButtonDisabled={savingTags}
        secondaryButtonAction={() => {
          setTagModalOpen(false);
          setLoadingExistingTags(false);
          reset();
        }}
        maxWidth="md"
      >
        <Box sx={{ overflowY: 'auto', pt: 2, pb: 2 }}>
          {loadingExistingTags ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="100px"
            >
              <Loader />
            </Box>
          ) : savingTags ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="100px"
              flexDirection="column"
              gap={2}
            >
              <Loader />
              <Typography variant="body2" color="text.secondary">
                Saving tags...
              </Typography>
            </Box>
          ) : (
            watchedTags.map((tag, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  alignItems: 'flex-start',
                  // minHeight: '80px', // Ensure consistent height for all rows
                }}
              >
                <InputElement
                  name={`tags.${index}.name`}
                  control={control}
                  label="Name"
                  size="small"
                  sx={{ flex: 1 }}
                  required
                  rules={{
                    required: 'Name is required',
                    pattern: {
                      value: /^[A-Za-z0-9_-]+$/,
                      message:
                        'Name can only contain alphanumeric characters, hyphens and underscores',
                    },
                  }}
                />
                <InputElement
                  name={`tags.${index}.value`}
                  control={control}
                  label="Value"
                  size="small"
                  sx={{ flex: 1 }}
                  required
                  rules={{
                    required: 'Value is required',
                    pattern: {
                      value: /^[A-Za-z0-9_-]+$/,
                      message:
                        'Value can only contain alphanumeric characters, hyphens and underscores',
                    },
                  }}
                />
                <Box display="flex" gap={1} sx={{ mt: 1 }}>
                  <CustomIconButton
                    buttonType="error"
                    tooltipTitle="Remove"
                    onClick={() => handleRemoveTag(index)}
                  >
                    <DeleteIcon />
                  </CustomIconButton>
                  {watchedTags.length < 10 && (
                    <CustomIconButton
                      buttonType="primary"
                      tooltipTitle="Add more"
                      onClick={handleAddMoreTags}
                    >
                      <AddCircleIcon />
                    </CustomIconButton>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </CustomModal>

      {/* Overlay Loader for saving tags */}
      <OverlayLoader loading={savingTags} text="Saving tags..." />

      {/* Rename Modal */}
      <CustomModal
        open={renameModalOpen}
        onClose={() => {
          setRenameModalOpen(false);
          resetRenameForm();
        }}
        type="info"
        title="Rename File"
        autoClose={false}
        primaryButtonLabel={renamingFile ? 'Renaming...' : 'Rename'}
        primaryButtonAction={handleRenameSubmit(handleRenameSubmitAction)}
        secondaryButtonLabel="Cancel"
        secondaryButtonAction={() => {
          setRenameModalOpen(false);
          resetRenameForm();
        }}
        primaryButtonDisabled={renamingFile}
        maxWidth="sm"
      >
        <Box sx={{ pt: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current file: <strong>{fileToRename?.name}</strong>
          </Typography>
          <InputElement
            name="newName"
            control={renameControl}
            label="New Name (without extension)"
            size="medium"
            fullWidth
            required
            rules={{
              required: 'New name is required',
              pattern: {
                value: fileNameWithoutExtensionValidation,
                message:
                  'Name must contain only valid characters (letters, numbers, spaces, hyphens, and underscores)',
              },
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Controller
              name="deleteOldVersions"
              control={renameControl}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      size="medium"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Delete old versions of{' '}
                      <strong>"{fileToRename?.name}"</strong>
                    </Typography>
                  }
                />
              )}
            />
          </Box>
        </Box>
      </CustomModal>

      {/* Overlay Loader for renaming */}
      <OverlayLoader loading={renamingFile} text="Renaming file..." />

      {/* Overlay Loader for copying files */}
      <OverlayLoader loading={copyingFiles} text="Copying files..." />

      {/* Overlay Loader for moving files */}
      <OverlayLoader loading={movingFiles} text="Moving files..." />
    </div>
  );
};

export default FileTable;
